// Serialize the store into the planner v2 v4 content payload. Element `id`s are
// kept in the wire format (the store needs them for keying and future
// collaboration merging will need them for stable identity). Hydrated image
// URLs are runtime values: the server resolves them from `imageId` on each load.

// Settings whitelist. `name` and `access` deliberately live at the plan level
// (DB columns), not inside content — content.settings is preferred over the DB
// columns on load, so leaking them here lets a stale copy win. (An old v2
// `snapStep` is dropped: snapping follows the minor grid, see model/grid.)
const SETTINGS_KEYS = ['showGrid', 'snapToGrid', 'showLabels', 'lockLabels', 'units', 'autoSave'];

function exportSettings(settings = {}) {
  const out = {};
  for (const key of SETTINGS_KEYS) {
    if (settings[key] !== undefined) out[key] = settings[key];
  }
  return out;
}

function exportElement(el, includeRuntimeImageUrls) {
  if (el.type !== 'image' || includeRuntimeImageUrls) return el;
  const { url, ...data } = el.data || {};
  return { ...el, data };
}

export function toContent(state, { includeRuntimeImageUrls = false } = {}) {
  return {
    version: 4,
    view: { scale: state.view.scale, posX: state.view.posX, posY: state.view.posY },
    settings: exportSettings(state.settings),
    elements: state.elements.map((el) => exportElement(el, includeRuntimeImageUrls)),
    layers: state.layers,
  };
}

// Drafts stay in this browser, so retain hydrated image URLs for an immediate
// restore. Rails payloads must use `toContent`, which removes those URLs.
export function toDraftContent(state) {
  return toContent(state, { includeRuntimeImageUrls: true });
}

export function toPlanPayload(state) {
  return {
    plan: {
      content: toContent(state),
      name: state.settings?.name ?? state.name,
      access: state.settings?.access,
    },
  };
}

// The localStorage draft — v1 Storage.save() parity: the content plus the plan
// name, the species cache (labels would degrade without it), a timestamp for
// draft-vs-server comparison, and name/access re-added inside settings (they
// are read from there when restoring a draft).
export function toDraft(state) {
  const draft = toDraftContent(state);
  const name = state.settings?.name ?? state.name ?? null;
  draft.name = name;
  draft.settings = { ...draft.settings, access: state.settings?.access, name };
  draft.species = state.species;
  draft.timestamp = Math.floor(Date.now() / 1000);
  return draft;
}

// A deep-cloned snapshot of the editable state for the undo stack.
export function snapshot(state) {
  return JSON.parse(JSON.stringify({
    elements: state.elements,
    layers: state.layers,
    settings: state.settings,
  }));
}
